import { Currency } from '@/features/dashboard/paymentConfig/types/Currency.ts';
import { TourBudgetViewDto } from '@/features/dashboard/tourBudget/types/TourBudgetDto.ts';
import { MoneyBreakdownDto } from '@/types/stats/BreakdownDto.ts';
import { sortByCurrency } from '@/types/stats/metric.utils.ts';

/**
 * Backend zwraca wpis także dla waluty, w której są same koszty, bez budżetu.
 * Rozróżnia je tylko para zer — innego znacznika w kontrakcie nie ma.
 */
export const isBudgetDeclared = (budget: TourBudgetViewDto): boolean =>
  budget.fixedAmount !== 0 || budget.perParticipantAmount !== 0;

export const budgetByCurrency = (
  budgets: TourBudgetViewDto[],
  currency: Currency,
): TourBudgetViewDto | undefined =>
  budgets.find((budget) => budget.currency === currency);

/** Waluty, w których da się jeszcze założyć budżet — lista wyboru w modalu. */
export const currenciesWithoutBudget = (
  budgets: TourBudgetViewDto[],
): Currency[] =>
  Object.values(Currency).filter((currency) => {
    const budget = budgetByCurrency(budgets, currency);
    return !budget || !isBudgetDeclared(budget);
  });

/**
 * Waluty kart budżetu to suma tych z dashboardu i tych z zadeklarowanym
 * budżetem. Dashboard pomija walutę, w której plan, zobowiązania i wydatki są
 * zerowe — budżet liczony wyłącznie na uczestnika znika w ten sposób, dopóki
 * nikt się nie zapisał, a nagłówek i tak nie proponuje go założyć drugi raz.
 */
export const budgetBreakdownsWithDeclared = (
  breakdowns: MoneyBreakdownDto[],
  budgets: TourBudgetViewDto[],
): MoneyBreakdownDto[] => {
  const omitted = budgets
    .filter(
      (budget) =>
        isBudgetDeclared(budget) &&
        !breakdowns.some((breakdown) => breakdown.currency === budget.currency),
    )
    .map(
      (budget): MoneyBreakdownDto => ({
        currency: budget.currency,
        total: 0,
        segments: [],
        metrics: [],
      }),
    );

  return sortByCurrency([...breakdowns, ...omitted]);
};
